import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, login, error } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);

  useEffect(() => {
    if (user) nav("/", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = await login(email, password);
    setLoading(false);
    if (ok) {
      toast.success("Signed in");
      nav("/", { replace: true });
    } else {
      toast.error("Sign-in failed");
    }
  };

  return (
    <div className="h-screen overflow-hidden grid md:grid-cols-2 bg-bone">
      {/* Visual panel */}
      <div className="relative hidden md:block border-r border-line overflow-hidden bg-[color:var(--aisensy-dark)]">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #03cf65 0%, transparent 40%), radial-gradient(circle at 80% 80%, #03cf65 0%, transparent 35%)",
          }}
        />
        <div className="relative z-10 h-full flex flex-col p-10 text-white">
          <div className="flex items-center gap-3">
            <img
              src="/brand/aisensy-light.svg"
              alt="AiSensy"
              className="h-7 w-auto"
            />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-white/70 border-l border-white/25 pl-3">
              Receivables
            </span>
          </div>
          <div className="flex-1 flex flex-col justify-center">
            <div className="eyebrow text-[color:var(--aisensy-green)]">Internal · Postpaid receivables</div>
            <h1 className="mt-3 font-display font-semibold text-4xl md:text-5xl leading-[1.05] tracking-tight">
              Track every invoice.
              <br />
              Collect on time.
            </h1>
            <p className="mt-4 text-white/70 max-w-md text-sm leading-relaxed">
              A dedicated cockpit for AiSensy&apos;s post-paid clients and partners. Attach
              invoices, share UTR references, and see who pays on time, at a glance.
            </p>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="eyebrow mb-2">Sign in</div>
          <h2 className="font-display font-semibold text-3xl tracking-tight mb-1">
            Welcome back
          </h2>
          <p className="text-sm text-ink-2 mb-8">
            Use your work email to access the dashboard.
          </p>

          <div className="space-y-4">
            {/* Google Sign-in — primary CTA */}
            {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button
              type="button"
              onClick={() => {
                const redirectUrl = window.location.origin + "/";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              data-testid="login-google-btn"
              className="w-full flex items-center justify-center gap-2 h-11 rounded-md border border-line bg-white hover:bg-[color:var(--bone)] text-ink font-semibold transition"
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.9 2.68-6.62z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.83.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.32A9 9 0 0 0 9 18z" fill="#34A853"/>
                <path d="M3.97 10.71a5.41 5.41 0 0 1 0-3.42V4.97H.96a9 9 0 0 0 0 8.06l3.01-2.32z" fill="#FBBC05"/>
                <path d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>

            <div className="flex items-center gap-3 text-[10px] text-ink-2 uppercase tracking-wider">
              <div className="flex-1 h-px bg-[color:var(--line)]" />
              or use password
              <div className="flex-1 h-px bg-[color:var(--line)]" />
            </div>

            <div>
              <Label htmlFor="email" className="eyebrow">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="rounded-sm mt-1"
                data-testid="login-email-input"
                placeholder="you@company.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="eyebrow">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="rounded-sm mt-1"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div className="mt-4 text-xs text-[color:var(--overdue)]" data-testid="login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full mt-6 rounded-md bg-[color:var(--aisensy-dark)] hover:bg-[color:var(--aisensy-dark-2)] text-white h-11"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <button
            type="button"
            data-testid="forgot-password-link"
            onClick={() => setShowForgot(true)}
            className="mt-4 text-xs text-ink-2 hover:text-[color:var(--aisensy-dark)] underline underline-offset-2"
          >
            Forgot password?
          </button>
        </form>
      </div>

      <Dialog open={showForgot} onOpenChange={setShowForgot}>
        <DialogContent className="rounded-sm max-w-md" data-testid="forgot-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reset your password</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-ink-2 space-y-3">
            <p>
              Password resets on this internal tool are admin-issued. Please ask your admin to
              generate a reset link for you:
            </p>
            <ol className="list-decimal ml-5 space-y-1 text-ink">
              <li>Admin opens the Team page.</li>
              <li>Clicks the key icon next to your name.</li>
              <li>Sends you the copied reset link (via WhatsApp, email or Slack).</li>
            </ol>
            <p>The link is valid for 24 hours. Opening it lets you set a new password.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
